import { useState } from 'react';
import { motion } from 'framer-motion';
import {
  Mail, Clock, Instagram, Send, CheckCircle, AlertCircle,
  Percent, Package, Truck, ShieldCheck, Headphones, ChevronDown, Building2,
} from 'lucide-react';
import { useTheme } from '../context/ThemeContext';
import SEO from '../components/SEO';
import Breadcrumb from '../components/Breadcrumb';
import { buildBreadcrumbJsonLd } from '../lib/seoSchema';
import { pageSeoMeta } from '@shared/pageSeoMeta';
import { wholesale, ApiError } from '../api/client';
import PhoneInput, { maxDigitsFor } from '../components/PhoneInput';
import { DEFAULT_COUNTRY, type Country } from '../data/countryCodes';

const PERKS = [
  { icon: <Percent size={20} className="text-emerald-500" />, title: 'Bulk Discounts', desc: 'Tiered trade pricing on volume' },
  { icon: <Package size={20} className="text-blue-500" />, title: 'Min. 100 pcs', desc: 'Low minimum to get started' },
  { icon: <Truck size={20} className="text-amber-500" />, title: 'Transport Arranged', desc: 'Logistics handled for you' },
  { icon: <ShieldCheck size={20} className="text-purple-500" />, title: '100% Authentic', desc: 'Original-market stock only' },
  { icon: <Headphones size={20} className="text-pink-500" />, title: 'Dedicated Support', desc: 'A direct line for partners' },
];

const WHOLESALE_FAQS = [
  {
    question: 'What is the minimum wholesale order quantity?',
    answer: 'Our wholesale minimum is 100 pieces per order. You can mix across flavours and strengths to reach the minimum.',
  },
  {
    question: 'Do you offer wholesale discounts?',
    answer: 'Yes. Wholesale orders receive tiered discounts based on quantity. Share your requirement and we will send you trade pricing.',
  },
  {
    question: 'Do you arrange transportation?',
    answer: 'Yes, we arrange transportation and logistics for bulk orders so your stock reaches you safely and on time.',
  },
  {
    question: 'How long does a bulk order take to deliver?',
    answer: 'Dispatch timelines depend on order size and destination. We confirm an estimated timeline when we share your quote.',
  },
  {
    question: 'Are wholesale products authentic?',
    answer: '100% authentic, original-market stock — the same products we sell retail, sourced directly with no grey-market goods.',
  },
];

const EMPTY = { name: '', businessName: '', phone: '', email: '', city: '', products: '', quantity: '', message: '', companyUrl: '' };

export default function WholesalePage() {
  const { isDark } = useTheme();
  const [form, setForm] = useState({ ...EMPTY });
  const [country, setCountry] = useState<Country>(DEFAULT_COUNTRY);
  const [validationError, setValidationError] = useState<string | null>(null);
  const [status, setStatus] = useState<'idle' | 'sending' | 'sent' | 'error' | 'rate'>('idle');
  const [openFAQ, setOpenFAQ] = useState<number | null>(0);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    setForm(prev => ({ ...prev, [e.target.name]: e.target.value }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const cleanPhone = form.phone.replace(/\D/g, '').slice(0, maxDigitsFor(country));
    if (country.iso === 'in') {
      if (cleanPhone.length !== 10) {
        setValidationError('Please enter a valid 10-digit phone number');
        return;
      }
    } else if (cleanPhone.length < 4) {
      setValidationError('Please enter a valid phone number');
      return;
    }
    setValidationError(null);
    setStatus('sending');
    try {
      await wholesale.create({
        name: form.name,
        business_name: form.businessName,
        phone: `${country.dialCode}${cleanPhone}`,
        email: form.email,
        city: form.city,
        products: form.products,
        quantity: Number(form.quantity) || 0,
        message: form.message,
        company_url: form.companyUrl,
      });
      setStatus('sent');
      setForm({ ...EMPTY });
    } catch (err) {
      if (err instanceof ApiError && err.status === 429) setStatus('rate');
      else setStatus('error');
    }
  };

  const inp = `w-full px-4 py-3 rounded-xl border text-sm outline-none transition-colors ${
    isDark
      ? 'bg-gray-800 border-gray-700 text-white placeholder-gray-500 focus:border-emerald-500'
      : 'bg-white border-gray-200 text-gray-900 placeholder-gray-400 focus:border-emerald-500'
  }`;

  const wholesaleFaqJsonLd = {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: WHOLESALE_FAQS.map((f) => ({
      '@type': 'Question',
      name: f.question,
      acceptedAnswer: { '@type': 'Answer', text: f.answer },
    })),
  };

  return (
    <div className="min-h-screen">
      <SEO
        {...pageSeoMeta.wholesale}
        jsonLd={[
          wholesaleFaqJsonLd,
          buildBreadcrumbJsonLd([
            { name: 'Home', url: '/' },
            { name: 'Wholesale', url: '/wholesale' },
          ]),
        ]}
      />

      <Breadcrumb items={[
        { name: 'Home', href: '/' },
        { name: 'Wholesale' },
      ]} />

      {/* Hero */}
      <section className="hero-gradient py-20">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}>
            <span className="text-emerald-400 text-sm font-semibold uppercase tracking-wider">For Retailers</span>
            <h1 className="text-4xl sm:text-5xl font-extrabold text-white mt-3 mb-6">Wholesale &amp; Bulk Orders</h1>
            <p className="text-gray-400 text-lg leading-relaxed max-w-2xl mx-auto">
              Stock your shop with authentic nicotine pouches at trade pricing. Tell us what you need and we'll get back to you with a quote.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Perks */}
      <section className={`py-12 transition-colors duration-300 ${isDark ? 'bg-gray-950' : 'bg-gray-50'}`}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-4">
            {PERKS.map((p, i) => (
              <motion.div
                key={p.title}
                initial={{ opacity: 0, y: 12 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.06 }}
                className={`rounded-xl p-4 ${isDark ? 'bg-gray-900' : 'bg-white shadow-sm'}`}
              >
                <div className={`w-10 h-10 rounded-lg flex items-center justify-center mb-3 ${isDark ? 'bg-gray-800' : 'bg-gray-50'}`}>
                  {p.icon}
                </div>
                <h3 className={`font-semibold text-sm ${isDark ? 'text-white' : 'text-gray-900'}`}>{p.title}</h3>
                <p className={`text-xs mt-0.5 ${isDark ? 'text-gray-500' : 'text-gray-500'}`}>{p.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Info + Form */}
      <section className={`py-20 transition-colors duration-300 ${isDark ? 'bg-gray-950' : 'bg-white'}`}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-5 gap-12">

            {/* Company info */}
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              className="lg:col-span-2 space-y-8"
            >
              <div>
                <h2 className={`text-2xl font-bold mb-2 ${isDark ? 'text-white' : 'text-gray-900'}`}>
                  Partner With Us
                </h2>
                <p className={`text-sm leading-relaxed ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
                  We supply retailers and bulk buyers with authentic, original-market nicotine pouches. Send us your requirement and our team will reach out with trade pricing and logistics.
                </p>
              </div>

              {[
                {
                  icon: <Mail size={20} className="text-emerald-500" />,
                  title: 'Email Us',
                  detail: 'zerosmoke2026@gmail.com',
                  sub: 'Best way to reach us',
                  href: 'mailto:zerosmoke2026@gmail.com',
                },
                {
                  icon: <Clock size={20} className="text-blue-500" />,
                  title: 'Response Time',
                  detail: 'Within 24 hours',
                  sub: 'Mon – Sat, 10 AM – 7 PM',
                },
                {
                  icon: <Instagram size={20} className="text-pink-500" />,
                  title: 'Instagram',
                  detail: '@zerosmoke.in_2026',
                  sub: 'DM us anytime',
                  href: 'https://www.instagram.com/zerosmoke.in_2026/',
                },
              ].map((item) => (
                <div key={item.title} className={`flex gap-4 p-4 rounded-xl ${isDark ? 'bg-gray-900' : 'bg-gray-50'}`}>
                  <div className={`w-10 h-10 rounded-lg flex items-center justify-center shrink-0 ${isDark ? 'bg-gray-800' : 'bg-white shadow-sm'}`}>
                    {item.icon}
                  </div>
                  <div>
                    <h3 className={`font-semibold text-sm ${isDark ? 'text-white' : 'text-gray-900'}`}>{item.title}</h3>
                    {item.href ? (
                      <a href={item.href} target="_blank" rel="noopener noreferrer" className="text-emerald-500 text-sm hover:underline">
                        {item.detail}
                      </a>
                    ) : (
                      <p className={`text-sm ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>{item.detail}</p>
                    )}
                    <p className={`text-xs mt-0.5 ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>{item.sub}</p>
                  </div>
                </div>
              ))}

              <div className={`p-4 rounded-xl border ${isDark ? 'border-gray-800 bg-gray-900/50' : 'border-gray-200 bg-gray-50'}`}>
                <div className="flex items-center gap-2 mb-2">
                  <Building2 size={16} className="text-amber-500" />
                  <h4 className={`font-semibold text-sm ${isDark ? 'text-white' : 'text-gray-900'}`}>Business Details</h4>
                </div>
                <p className={`text-xs leading-relaxed ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
                  Registered business & GST information available on request once we connect.
                </p>
              </div>
            </motion.div>

            {/* Inquiry form */}
            <motion.div
              initial={{ opacity: 0, x: 20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              className="lg:col-span-3"
            >
              <div className={`rounded-2xl p-6 sm:p-8 ${isDark ? 'bg-gray-900 border border-gray-800' : 'bg-white border border-gray-200 shadow-sm'}`}>
                <h3 className={`text-lg font-bold mb-6 ${isDark ? 'text-white' : 'text-gray-900'}`}>Request a Wholesale Quote</h3>

                {status === 'rate' && (
                  <div className="flex items-center gap-2 bg-amber-500/10 text-amber-500 p-3 rounded-lg mb-4 text-sm">
                    <AlertCircle size={16} /> Too many requests. Please try again in a few minutes.
                  </div>
                )}
                {status === 'error' && (
                  <div className="flex items-center gap-2 bg-red-500/10 text-red-500 p-3 rounded-lg mb-4 text-sm">
                    <AlertCircle size={16} /> Something went wrong. Please email us directly at zerosmoke2026@gmail.com
                  </div>
                )}
                {validationError && (
                  <div className="flex items-center gap-2 bg-red-500/10 text-red-500 p-3 rounded-lg mb-4 text-sm">
                    <AlertCircle size={16} /> {validationError}
                  </div>
                )}

                <form onSubmit={handleSubmit} className="space-y-4">
                  {/* Honeypot — hidden from humans, catches bots */}
                  <input
                    type="text"
                    name="companyUrl"
                    value={form.companyUrl}
                    onChange={handleChange}
                    tabIndex={-1}
                    autoComplete="off"
                    aria-hidden="true"
                    style={{ display: 'none' }}
                  />

                  <div className="grid sm:grid-cols-2 gap-4">
                    <div>
                      <label className={`block text-xs font-medium mb-1.5 ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>Your Name <span className="text-red-500">*</span></label>
                      <input type="text" name="name" value={form.name} onChange={handleChange} placeholder="John Doe" className={inp} required maxLength={100} />
                    </div>
                    <div>
                      <label className={`block text-xs font-medium mb-1.5 ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>Business / Shop Name <span className="text-red-500">*</span></label>
                      <input type="text" name="businessName" value={form.businessName} onChange={handleChange} placeholder="Doe Retail" className={inp} required maxLength={150} />
                    </div>
                  </div>

                  <div className="grid sm:grid-cols-2 gap-4">
                    <div>
                      <label className={`block text-xs font-medium mb-1.5 ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>Phone <span className="text-red-500">*</span></label>
                      <PhoneInput
                        country={country}
                        onCountryChange={setCountry}
                        value={form.phone}
                        onChange={phone => setForm(prev => ({ ...prev, phone }))}
                        isDark={isDark}
                        required
                      />
                    </div>
                    <div>
                      <label className={`block text-xs font-medium mb-1.5 ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>Email <span className="text-red-500">*</span></label>
                      <input type="email" name="email" value={form.email} onChange={handleChange} placeholder="you@example.com" className={inp} required maxLength={150} />
                    </div>
                  </div>

                  <div className="grid sm:grid-cols-2 gap-4">
                    <div>
                      <label className={`block text-xs font-medium mb-1.5 ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>City <span className="text-red-500">*</span></label>
                      <input type="text" name="city" value={form.city} onChange={handleChange} placeholder="Your city" className={inp} required maxLength={100} />
                    </div>
                    <div>
                      <label className={`block text-xs font-medium mb-1.5 ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>Estimated Quantity (pcs) <span className="text-red-500">*</span></label>
                      <input type="number" name="quantity" value={form.quantity} onChange={handleChange} placeholder="100" min={100} step={1} className={inp} required />
                    </div>
                  </div>

                  <div>
                    <label className={`block text-xs font-medium mb-1.5 ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>Products Interested In <span className="text-red-500">*</span></label>
                    <input type="text" name="products" value={form.products} onChange={handleChange} placeholder="e.g. ZYN Cool Mint, VELO Ice Cool, mixed brands" className={inp} required maxLength={300} />
                  </div>

                  <div>
                    <label className={`block text-xs font-medium mb-1.5 ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>Message <span className="text-red-500">*</span></label>
                    <textarea name="message" value={form.message} onChange={handleChange} placeholder="Tell us about your requirement, frequency, and any questions..." rows={4} className={`${inp} resize-none`} required maxLength={2000} />
                  </div>

                  <button
                    type="submit"
                    disabled={status === 'sending'}
                    className="w-full flex items-center justify-center gap-2 py-3 bg-emerald-500 hover:bg-emerald-600 disabled:opacity-50 text-white font-semibold rounded-xl text-sm transition-colors"
                  >
                    {status === 'sending' ? (
                      <><div className="animate-spin w-4 h-4 border-2 border-white border-t-transparent rounded-full" /> Sending...</>
                    ) : (
                      <><Send size={16} /> Send Inquiry</>
                    )}
                  </button>

                  <p className={`text-xs text-center ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>
                    Minimum wholesale order is 100 pcs. You'll receive a confirmation email once we get your inquiry.
                  </p>
                </form>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className={`py-16 transition-colors duration-300 ${isDark ? 'bg-gray-900' : 'bg-gray-50'}`}>
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className={`text-2xl sm:text-3xl font-extrabold text-center mb-10 ${isDark ? 'text-white' : 'text-gray-900'}`}>
            Wholesale <span className="text-gradient">FAQ</span>
          </h2>
          <div className="space-y-3">
            {WHOLESALE_FAQS.map((faq, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, y: 10 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.04 }}
                className={`rounded-2xl border overflow-hidden transition-all ${
                  isDark
                    ? `bg-gray-800 ${openFAQ === i ? 'border-emerald-500/50' : 'border-gray-700'}`
                    : `bg-white ${openFAQ === i ? 'border-emerald-500 shadow-lg shadow-emerald-600/5' : 'border-gray-100'}`
                }`}
              >
                <button
                  onClick={() => setOpenFAQ(openFAQ === i ? null : i)}
                  className="w-full flex items-center justify-between p-5 text-left"
                  aria-expanded={openFAQ === i}
                >
                  <span className={`font-semibold pr-4 ${isDark ? 'text-white' : 'text-gray-900'}`}>
                    {faq.question}
                  </span>
                  <motion.span animate={{ rotate: openFAQ === i ? 180 : 0 }} transition={{ duration: 0.3 }}>
                    <ChevronDown size={18} className="text-gray-400" />
                  </motion.span>
                </button>
                <motion.div
                  initial={false}
                  animate={{ height: openFAQ === i ? 'auto' : 0, opacity: openFAQ === i ? 1 : 0 }}
                  transition={{ duration: 0.3 }}
                  className="overflow-hidden"
                  aria-hidden={openFAQ !== i}
                >
                  <p className={`px-5 pb-5 text-sm sm:text-base leading-relaxed ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
                    {faq.answer}
                  </p>
                </motion.div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Success modal */}
      {status === 'sent' && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4"
          onClick={() => setStatus('idle')}
        >
          <motion.div
            initial={{ opacity: 0, scale: 0.92, y: 10 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            transition={{ duration: 0.25 }}
            onClick={e => e.stopPropagation()}
            className={`w-full max-w-sm rounded-2xl border p-8 text-center ${isDark ? 'bg-gray-900 border-gray-800' : 'bg-white border-gray-200'}`}
          >
            <div className="w-16 h-16 rounded-full bg-emerald-500/10 flex items-center justify-center mx-auto mb-5">
              <CheckCircle size={32} className="text-emerald-500" />
            </div>
            <h3 className={`text-xl font-bold mb-2 ${isDark ? 'text-white' : 'text-gray-900'}`}>Thank You!</h3>
            <p className={`text-sm leading-relaxed mb-6 ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
              We've received your wholesale inquiry and will get back to you soon. A confirmation email is on its way.
            </p>
            <button
              onClick={() => setStatus('idle')}
              className="w-full py-3 bg-emerald-500 hover:bg-emerald-600 text-white font-semibold rounded-xl text-sm transition-colors"
            >
              OK
            </button>
          </motion.div>
        </div>
      )}
    </div>
  );
}
